import { AbstractAd } from '@lib/iab/iab-vast-model/ad/abstract';

import createAdSystem from '../factory/ad-system';
import createCreative from '../factory/creative';
import createExtension from '../factory/extension';
import createImpression from '../factory/impression';
import createPricing from '../factory/pricing';
import createVerification from '../factory/verification';
import createViewableImpression from '../factory/viewable-impression';
import isNonEmptyString from '../util/is-non-empty-string';

interface UnmarshalerNode {
    _value: unknown;
    [key: string]: unknown;
}

interface ParseOptions {
    errorHandler: {
        tryRecover: (error: Error) => void;
    };
}

const hasValue = ($node: UnmarshalerNode): boolean =>
    $node != null && isNonEmptyString($node._value);

const createAd = (
    $ad: UnmarshalerNode,
    $impl: UnmarshalerNode,
    ad: AbstractAd,
    options: ParseOptions
): AbstractAd => {
    ad.id = $ad.id as string;
    ad.conditionalAd = $ad.conditionalAd as boolean;
    ad.sequence = $ad.sequence as number;

    if ($impl.adSystem != null) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ad.adSystem = createAdSystem($impl.adSystem as any);
    }

    if ($impl.impression != null) {
        ad.impressions.push(
            ...($impl.impression as UnmarshalerNode[])
                .filter(hasValue)
                .map((impression: UnmarshalerNode) =>
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    createImpression(impression as any)
                )
        );
    }

    if ($impl.pricing != null) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ad.pricing = createPricing($impl.pricing as any);
    }

    if ($impl.error != null) {
        ad.errors.push(
            ...($impl.error as UnmarshalerNode[])
                .filter(hasValue)
                .map(($err) => $err._value as string)
        );
    }

    if ($impl.viewableImpression != null) {
        ad.viewableImpression = createViewableImpression(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            $impl.viewableImpression as any
        );
    }

    if ($impl.adVerifications != null) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ($impl.adVerifications as any).verification.forEach(
            ($verification: UnmarshalerNode) => {
                try {
                    const verification = createVerification($verification);
                    ad.verifications.push(verification);
                } catch (error) {
                    options.errorHandler.tryRecover(error as Error);
                }
            }
        );
    }

    if ($impl.extensions != null) {
        ad.extensions.push(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ...($impl.extensions as any).extension.map(createExtension)
        );
    }

    if ($impl.creatives != null) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ($impl.creatives as any).creative.forEach(
            ($creative: UnmarshalerNode) => {
                try {
                    const creative = createCreative($creative, options);
                    ad.creatives.add(creative);
                } catch (error) {
                    options.errorHandler.tryRecover(error as Error);
                }
            }
        );
    }

    return ad;
};

export default createAd;
